"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserPlus,
  MoreHorizontal,
  Trash2,
  UserCog,
  Mail,
  Calendar,
  Shield,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { PermissionUser } from "@/services/permissionService";
import { Avatar } from "@/components/ui/avatar";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchPermissionUsersRequest,
  updatePermissionUserAccessRequest,
  removePermissionUserRequest,
  clearPermissionActionState,
} from "@/store/slices/permission.slice";

type User = PermissionUser & {
  status: "active" | "pending" | "inactive";
  invitedAt?: string;
  invitedBy?: string;
  role: string;
  lastActive?: string;
  displayIdentity: string;
};

interface AddUserForm {
  email: string;
  role: string;
}

type StoreType = "seller" | "wholesaler";

const ROLE_OPTIONS_BY_STORE_TYPE: Record<
  StoreType,
  Array<{ value: string; label: string; description: string }>
> = {
  seller: [
    {
      value: "seller",
      label: "Seller",
      description: "Manage products, orders, and sales",
    },
    {
      value: "order_manager",
      label: "Order Manager",
      description: "Manage and process store orders",
    },
    { value: "staff", label: "Staff", description: "Basic staff access" },
  ],
  wholesaler: [
    {
      value: "wholesaler",
      label: "Wholesaler",
      description: "Access wholesale features and pricing",
    },
    {
      value: "order_manager",
      label: "Order Manager",
      description: "Manage and process store orders",
    },
    { value: "staff", label: "Staff", description: "Basic staff access" },
  ],
};

const Permission = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { storeId } = params || {};
  const [pageIndex, setPageIndex] = useState(0);
  const [filters] = useState<{
    accessLevel?: "seller" | "wholesaler" | "order_manager" | "staff";
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }>({
    sortBy: "created_at",
    sortOrder: "desc",
  });

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addUserForm, setAddUserForm] = useState<AddUserForm>({
    email: "",
    role: "",
  });
  const [newRole, setNewRole] = useState("");

  const { items, loaded, loading, error, pagination, actionState } =
    useAppSelector((state) => state.permissions);
  const currentStoreType = useAppSelector(
    (state) => state.auth.currentStoreInfo?.storeType,
  );
  const roles = useMemo(() => {
    if (currentStoreType === "seller" || currentStoreType === "wholesaler") {
      return ROLE_OPTIONS_BY_STORE_TYPE[currentStoreType];
    }
    return ROLE_OPTIONS_BY_STORE_TYPE.seller;
  }, [currentStoreType]);

  // Convert API user to display format
  const formatUserForDisplay = (apiUser: PermissionUser): User => {
    const rawRole = apiUser.current_role || apiUser.access_level || "staff";
    const roleMap = {
      seller: "Seller",
      wholesaler: "Wholesaler",
      order_manager: "Order Manager",
      staff: "Staff",
    };

    const displayIdentity =
      apiUser.email || apiUser.phone_number || apiUser.name || apiUser.id;

    return {
      ...apiUser,
      role: roleMap[rawRole] || "Staff",
      status: "active" as const, // API doesn't return status, assuming active
      invitedAt: new Date(apiUser.created_at).toLocaleDateString(),
      invitedBy: apiUser.invited_by || "System",
      lastActive: apiUser.last_active
        ? new Date(apiUser.last_active).toLocaleDateString()
        : undefined,
      displayIdentity,
    };
  };

  const users = useMemo(() => items.map(formatUserForDisplay), [items]);

  const fetchUsers = useCallback(() => {
    if (!storeId) return;

    dispatch(
      fetchPermissionUsersRequest({
        storeId: storeId as string,
        params: {
          page: pageIndex + 1,
          page_size: pagination.pageSize || 10,
          access_level: filters.accessLevel,
          sort_by: filters.sortBy,
          sort_order: filters.sortOrder,
        },
      }),
    );
  }, [dispatch, storeId, pageIndex, pagination.pageSize, filters]);

  useEffect(() => {
    if (!storeId || loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && items.length === 0) {
      fetchUsers();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchUsers();
    }
  }, [
    storeId,
    loaded,
    loading,
    error?.status,
    pagination.page,
    pageIndex,
    items.length,
    fetchUsers,
  ]);

  useEffect(() => {
    if (actionState.successMessage) {
      toast.success(actionState.successMessage);

      if (actionState.successMessage.includes("role updated")) {
        setIsChangeRoleModalOpen(false);
        setSelectedUser(null);
        setNewRole("");
      }

      dispatch(clearPermissionActionState());
    }
  }, [actionState.successMessage, dispatch]);

  useEffect(() => {
    if (actionState.error?.message) {
      toast.error(actionState.error.message);
      dispatch(clearPermissionActionState());
    }
  }, [actionState.error, dispatch]);

  useEffect(() => {
    if (error?.message) {
      toast.error(error.message);
    }
  }, [error]);

  const getStatusBadge = (status: User["status"]) => {
    const statusConfig = {
      active: {
        color: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
        label: "Active",
      },
      pending: {
        color: "bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20",
        label: "Pending",
      },
      inactive: {
        color: "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20",
        label: "Inactive",
      },
    };

    return (
      <Badge className={statusConfig[status].color}>
        {statusConfig[status].label}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    const roleColors = {
      Seller: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      Wholesaler: "bg-purple-500/20 text-purple-600 hover:bg-purple-500/20",
      "Order Manager": "bg-cyan-500/20 text-cyan-600 hover:bg-cyan-500/20",
      Staff: "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20",
    };

    return (
      <Badge
        className={
          roleColors[role as keyof typeof roleColors] || roleColors.Staff
        }
      >
        {role}
      </Badge>
    );
  };

  const handleAddUser = () => {
    // TODO: Implement add user functionality when invite endpoint is available
    if (addUserForm.email && addUserForm.role) {
      toast.info(
        "Add user functionality will be implemented when invite endpoint is available",
      );
      setAddUserForm({ email: "", role: "" });
      setIsAddUserModalOpen(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!storeId) return;

    dispatch(
      removePermissionUserRequest({ storeId: storeId as string, userId }),
    );
  };

  const handleChangeRole = async () => {
    if (!storeId || !selectedUser || !newRole) return;

    dispatch(
      updatePermissionUserAccessRequest({
        storeId: storeId as string,
        userId: selectedUser.id,
        accessLevel: newRole as "seller" | "wholesaler" | "order_manager" | "staff",
      }),
    );
  };

  useEffect(() => {
    if (newRole && !roles.some((role) => role.value === newRole)) {
      setNewRole("");
    }
  }, [newRole, roles]);

  useEffect(() => {
    if (addUserForm.role && !roles.some((role) => role.value === addUserForm.role)) {
      setAddUserForm((prev) => ({ ...prev, role: "" }));
    }
  }, [addUserForm.role, roles]);

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "email",
      header: "User",
      minSize: 300,
      size: 350,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center gap-3">
            <Avatar
              name={user.email ?? user.phone_number ?? "User"}
              className="h-10 w-10"
            />
            <div>
              <p className="font-medium text-foreground">
                {user.displayIdentity}
              </p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Joined {user.invitedAt ?? "N/A"}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            {getRoleBadge(row.original.role)}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="space-y-1">
            {getStatusBadge(user.status)}
            {user.lastActive && user.status === "active" && (
              <p className="text-xs text-muted-foreground">
                Last active: {user.lastActive}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "invitedBy",
      header: "Invited By",
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {row.original.invitedBy || "System"}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedUser(user);
                  setNewRole(user.current_role || user.access_level || "staff");
                  setIsChangeRoleModalOpen(true);
                }}
              >
                <UserCog className="mr-2 h-4 w-4" />
                Change Role
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleRemoveUser(user.id)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: (
        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
      ),
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add User",
      icon: <UserPlus className="h-3 w-3" />,
      onClick: () => setIsAddUserModalOpen(true),
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="User Management"
            description="Manage user permissions and access levels"
            columns={columns}
            data={users}
            pagination={{
              page: pagination.page,
              totalPages: pagination.totalPages,
              pageSize: pagination.pageSize,
              total: pagination.total,
            }}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={loading}
            emptyStateType="customers"
            actionButtons={actionButtons}
            enableColumnResizing={false}
          />
        </div>
      </TableContainer>

      {/* Add User Modal */}
      <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Send an invitation to a new user. They will receive an email with
              instructions to join.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter user's email address"
                value={addUserForm.email}
                onChange={(e) =>
                  setAddUserForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={addUserForm.role}
                onValueChange={(value) =>
                  setAddUserForm((prev) => ({ ...prev, role: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <p className="font-medium">{role.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {role.description}
                        </p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddUserModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAddUser}>Send Invitation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Modal */}
      <Dialog
        open={isChangeRoleModalOpen}
        onOpenChange={setIsChangeRoleModalOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.email}. This will change their
              access permissions immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newRole">New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a new role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <p className="font-medium">{role.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {role.description}
                        </p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsChangeRoleModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleChangeRole}>Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Permission;
